import { useEffect } from 'react';
import { useSetRecoilState } from 'recoil';
import { agentCodeTestConfigPlan } from '../../../../atoms/atoms';
import type {
  AgentCodeTestConfigChange,
  AgentTestOperation,
  PlanApprovalStatus,
} from '../../../../types';
import { pluralizeString } from '../../../../utils/common';
import type { ApprovalToolRendererProps } from '../../components/ApprovalToolRenderer';
import { createPlanApprovalRenderer } from '../applyTestConfigPlanShared/ApplyTestConfigPlanToolRenderer';
import { TEST_MANAGEMENT_PATH } from '../applyTestConfigPlanShared/sharedUtils';
import {
  formatCodeTestChangeLabel,
  formatCodeTestChangeSummary,
  parseCodeTestPlanResult,
} from './utils';

const PREVIEW_PATH = `${TEST_MANAGEMENT_PATH}/code-agent-preview`;

const getHeaderLabel = (
  status: PlanApprovalStatus | undefined,
  operation: AgentTestOperation,
  count: number,
): string => {
  if (status === 'confirmed') return 'Plan approved';
  if (status === 'rejected') return 'Plan rejected';
  if (operation === 'create') {
    return `Add ${pluralizeString(count, 'test', 'tests')}`;
  }
  if (operation === 'delete') {
    return `Delete ${pluralizeString(count, 'test', 'tests')}`;
  }
  return `Edit ${pluralizeString(count, 'test', 'tests')}`;
};

const getApproveLabel = () => 'Open PR';

const BaseRenderer = createPlanApprovalRenderer<AgentCodeTestConfigChange>({
  planAtom: agentCodeTestConfigPlan,
  previewPath: PREVIEW_PATH,
  returnPath: TEST_MANAGEMENT_PATH,
  formatChangeLabel: formatCodeTestChangeLabel,
  formatChangeSummary: formatCodeTestChangeSummary,
  getHeaderLabel,
  getApproveLabel,
});

const ApplyCodeTestConfigPlanToolRendererInner = (
  props: ApprovalToolRendererProps,
) => {
  const setPlan = useSetRecoilState(agentCodeTestConfigPlan);
  const toolCallId = props.invocation.toolCallId;
  const parsedResult = parseCodeTestPlanResult(props.invocation.result?.result);
  const draftPr = parsedResult?.draftPr;

  useEffect(() => {
    if (!draftPr) return;
    setPlan((prev) => {
      if (!prev || prev.toolCallId !== toolCallId || prev.draftPr) return prev;
      return { ...prev, draftPr };
    });
  }, [draftPr, setPlan, toolCallId]);

  return <BaseRenderer {...props} />;
};

// Called as a plain function by ToolInvocationRenderer rather than as JSX, so
// this wrapper must not use hooks — only return an element that owns them.
const ApplyCodeTestConfigPlanToolRenderer = (
  props: ApprovalToolRendererProps,
) => <ApplyCodeTestConfigPlanToolRendererInner {...props} />;

export default ApplyCodeTestConfigPlanToolRenderer;
